const SCOPE_CLASS = '.hum-style-root';

export function scopeCustomCss(rawCss, includeWrapperSelectors = false) {
   const normalized = normalizeCustomCss(rawCss);
   if (!normalized) {
      return '';
   }

   const css = stripCssComments(normalized);
   try {
      return scopeCssBlock(css, includeWrapperSelectors);
   } catch (e) {
      return '';
   }
}

function normalizeCustomCss(rawCss) {
   if (!rawCss || typeof rawCss !== 'string') {
      return '';
   }
   return rawCss.replace(/^\uFEFF/, '')
      .replace(/\r\n/g, '\n')
      .replace(/\r/g, '\n')
      .trim();
}

function stripCssComments(css) {
   return css.replace(/\/\*[\s\S]*?\*\//g, '').trim();
}

function scopeCssBlock(css, includeWrapperSelectors) {
   let i = 0;
   let out = '';

   while (i < css.length) {
      while (i < css.length && isWhitespace(css[i])) {
         i += 1;
      }
      if (i >= css.length) {
         break;
      }

      const open = css.indexOf('{', i);
      if (open === -1) {
         throw new Error('Invalid CSS');
      }

      const selector = css.slice(i, open).trim();
      if (!selector) {
         throw new Error('Invalid CSS');
      }

      const close = findMatchingBrace(css, open);
      const body = css.slice(open + 1, close).trim();

      if (selector.trim().startsWith('@')) {
         const scopedBody = scopeCssBlock(body, includeWrapperSelectors);
         out += `${selector}{${scopedBody}}`;
      } else {
         const scopedSelector = scopeSelectorList(selector, includeWrapperSelectors);
         out += `${scopedSelector}{${body}}`;
      }

      i = close + 1;
   }

   return out;
}

function findMatchingBrace(css, start) {
   let depth = 0;
   let inString = null;
   let escaped = false;

   for (let i = start; i < css.length; i += 1) {
      const c = css[i];

      if (inString) {
         if (escaped) {
            escaped = false;
            continue;
         }
         if (c === '\\') {
            escaped = true;
            continue;
         }
         if (c === inString) {
            inString = null;
         }
         continue;
      }

      if (c === '"' || c === "'") {
         inString = c;
         continue;
      }

      if (c === '{') {
         depth += 1;
      } else if (c === '}') {
         depth -= 1;
         if (depth === 0) {
            return i;
         }
      }
   }

   throw new Error('Invalid CSS');
}

function scopeSelectorList(selector, includeWrapperSelectors) {
   const selectors = splitSelectors(selector);
   const scoped = selectors
      .map((sel) => scopeSelector(sel.trim(), includeWrapperSelectors))
      .filter((sel) => sel.length > 0);

   return scoped.join(', ');
}

function scopeSelector(selector, includeWrapperSelectors) {
   const trimmed = selector.trim();
   const lower = trimmed.toLowerCase();
   const scopeLower = SCOPE_CLASS.toLowerCase();

   if (lower.startsWith(scopeLower)) {
      return trimmed;
   }

   if (lower === '*') {
      return `${SCOPE_CLASS} *`;
   }

   const roots = ['html', 'body', ':root'];
   for (const root of roots) {
      if (lower === root) {
         return SCOPE_CLASS;
      }
      if (lower.startsWith(`${root} `) || lower.startsWith(`${root}>`) || lower.startsWith(`${root}+`) || lower.startsWith(`${root}~`)) {
         const remainder = trimmed.slice(root.length).trim();
         return remainder ? `${SCOPE_CLASS} ${remainder}` : SCOPE_CLASS;
      }
   }

   // Campaign classes can live on the scope root itself as well as descendants.
   if (includeWrapperSelectors && /^\.(swal-wrapper|hum-(modal|banner|slide_in_box|inline|success-message))(?=[\s.#[:>+~]|$)/.test(trimmed)) {
      return `${SCOPE_CLASS}${trimmed}, ${SCOPE_CLASS} ${trimmed}`;
   }
   return `${SCOPE_CLASS} ${trimmed}`;
}

function splitSelectors(selector) {
   const res = [];
   let buf = '';
   let inString = null;
   let bracketDepth = 0;
   let parenDepth = 0;

   for (let i = 0; i < selector.length; i += 1) {
      const c = selector[i];

      if (inString) {
         buf += c;
         if (c === '\\' && i + 1 < selector.length) {
            i += 1;
            buf += selector[i];
         } else if (c === inString) {
            inString = null;
         }
         continue;
      }

      switch (c) {
         case '"':
         case "'":
            inString = c;
            buf += c;
            break;
         case '[':
            bracketDepth += 1;
            buf += c;
            break;
         case ']':
            if (bracketDepth > 0) {
               bracketDepth -= 1;
            }
            buf += c;
            break;
         case '(':
            parenDepth += 1;
            buf += c;
            break;
         case ')':
            if (parenDepth > 0) {
               parenDepth -= 1;
            }
            buf += c;
            break;
         case ',':
            if (bracketDepth === 0 && parenDepth === 0) {
               const part = buf.trim();
               if (part) {
                  res.push(part);
               }
               buf = '';
            } else {
               buf += c;
            }
            break;
         default:
            buf += c;
      }
   }

   const last = buf.trim();
   if (last) {
      res.push(last);
   }

   return res;
}

function isWhitespace(char) {
   return char === ' ' || char === '\t' || char === '\n' || char === '\r';
}
